import { DataProvider } from "react-admin";
import {
  decodeRoute,
  getAddressFromCoordinates,
  getRouteService,
} from "utils/tools";
import moment from "moment";
import dataProvider from "../../dataProvider";

const delay = (ms: number) => new Promise((res) => setTimeout(res, ms));

export const getStudents = async (
  levelId: Number,
  isCcaSchedule: boolean,
  isRegularSchedule: boolean,
  provider: DataProvider,
) => {
  const filter: any = {
    LevelId: levelId,
    Status: "active",
  };

  if (isCcaSchedule) {
    filter.HasCcaSchedule = isCcaSchedule;
  }

  if (isRegularSchedule) {
    filter.HasRegularSchedule = isRegularSchedule;
  }

  return await provider
    .getList("students", {
      filter,
      pagination: {
        page: 1,
        perPage: 9999,
      },
      sort: {
        field: "Id",
        order: "ASC",
      },
    })
    .then((result) => {
      return result.data;
    });
};

export const getStudentAddressesByStudentIds = async (
  studentIds: Number[],
  provider: DataProvider,
) => {
  return await provider
    .getList("studentAddresses", {
      filter: { StudentIds: studentIds },
      pagination: {
        page: 1,
        perPage: 1000,
      },
      sort: {
        field: "Id",
        order: "ASC",
      },
    })
    .then((result) => {
      return result.data;
    });
};

export const getFixedLocationDropOffBySchoolIds = async (
  schoolId: Number,
  provider: DataProvider,
) => {
  return await provider
    .getList("ccaFixedLocationDropOffs", {
      filter: { SchoolId: schoolId, Status: "active" },
      pagination: {
        page: 1,
        perPage: 1000,
      },
      sort: {
        field: "Id",
        order: "ASC",
      },
    })
    .then((result) => {
      return result.data;
    });
};

export const getSubcontractorsByIds = async (
  provider: DataProvider,
  subcontractorIds: number[],
) => {
  try {
    const result = await provider.getMany("subcontractors", {
      ids: subcontractorIds,
    });

    return result.data;
  } catch (error) {
    console.error("Failed to fetch subcontractors:", error);
    return [];
  }
};

export const getDriversByIds = async (
  provider: DataProvider,
  driverIds: number[],
) => {
  try {
    const result = await provider.getMany("drivers", {
      ids: driverIds,
    });

    return result.data;
  } catch (error) {
    console.error("Failed to fetch drivers:", error);
    return [];
  }
};

export const getBusAssistantsByIds = async (
  provider: DataProvider,
  busAssistantIds: number[],
) => {
  try {
    const result = await provider.getMany("busAssistants", {
      ids: busAssistantIds,
    });

    return result.data;
  } catch (error) {
    console.error("Failed to fetch bus assistants:", error);
    return [];
  }
};

export const getVehiclesByIds = async (
  provider: DataProvider,
  vehicleIds: number[],
) => {
  try {
    const result = await provider.getMany("vehicles", {
      ids: vehicleIds,
    });

    return result.data;
  } catch (error) {
    console.error("Failed to fetch vehicles:", error);
    return [];
  }
};

export const getDriverName = (drivers: any[], driverId: number) => {
  const driver = drivers.find((d) => d.id === driverId);

  if (!driver) return "-";

  const contactNumber = driver.contactNumber || "N/A";
  return `${driver.fullName} - ${contactNumber}`;
};

export const getSubcontractorName = (
  subcontractors: any[],
  subcontractorId: number,
) => {
  const subcontractor = subcontractors.find(
    (sc: any) => sc.id === subcontractorId,
  );

  if (!subcontractor) return "-";

  const identifier = subcontractor.subcontractorIdentifier || "N/A";
  return `${subcontractor.name} - ${identifier}`;
};

export const getBusAssistantName = (
  busAssistants: any[],
  busAssistantId: number,
) => {
  const busAssistant = busAssistants.find(
    (ba: any) => ba.id === busAssistantId,
  );

  if (!busAssistant) return "-";

  const contactNumber = busAssistant.contactNumber || "N/A";
  return `${busAssistant.fullName} - ${contactNumber}`;
};

export const getVehiclePlateNumber = (vehicles: any[], vehicleId: number) => {
  return vehicles.find((vehicle: any) => vehicle.id === vehicleId)?.plateNumber;
};

export const colors = [
  { colorOne: "#78B9FF", colorTwo: "#127EF1" }, // Original
  { colorOne: "#6EE7B7", colorTwo: "#10B981" }, // Emerald Green
  { colorOne: "#FBBF24", colorTwo: "#F59E0B" }, // Amber Orange
  { colorOne: "#A78BFA", colorTwo: "#8B5CF6" }, // Purple
  { colorOne: "#F87171", colorTwo: "#EF4444" }, // Rose Red
  { colorOne: "#C4B5FD", colorTwo: "#818CF8" }, // Indigo
  { colorOne: "#FDCEDF", colorTwo: "#F9A8D4" }, // Pink
  { colorOne: "#FDE68A", colorTwo: "#FBBF24" }, // Yellow
  { colorOne: "#A5F3FC", colorTwo: "#22D3EE" }, // Cyan
  { colorOne: "#A2F9F9", colorTwo: "#0FC2C0" }, // Teal
  { colorOne: "#D9F99D", colorTwo: "#84CC16" }, // Lime
  { colorOne: "#BAE6FD", colorTwo: "#0EA5E9" }, // Sky Blue
  { colorOne: "#FFE4E6", colorTwo: "#FB7185" }, // Rose
  { colorOne: "#FFE066", colorTwo: "#FBBF24" }, // Amber
  { colorOne: "#C4B5FD", colorTwo: "#7C3AED" }, // Violet
  { colorOne: "#A3E635", colorTwo: "#22C55E" }, // Emerald
  { colorOne: "#A5F3FC", colorTwo: "#0D9488" }, // Cyan
  { colorOne: "#C7D2FE", colorTwo: "#6366F1" }, // Indigo
  { colorOne: "#FBCFE8", colorTwo: "#DB2777" }, // Pink
];

export const getAddressLocation = async (lat: number, lng: number) => {
  try {
    const { GeocodeInfo } = await getAddressFromCoordinates({ lat, lng });
    return GeocodeInfo ? GeocodeInfo[0] : null;
  } catch (e) {
    throw e;
  }
};

export const getRoutePolyline = async (
  selectedRouteLocations: any[],
  onSegmentFetch?: () => void, // <-- Callback for progress update
  signal?: AbortSignal,
) => {
  try {
    const routeSegments: {
      polyline: any[];
      totalTime: number;
      routeGeometry: any;
    }[] = [];

    for (let i = 0; i < selectedRouteLocations.length - 1; i++) {
      if (signal?.aborted) {
        throw new Error("Request aborted");
      }

      const start = selectedRouteLocations[i];
      const end = selectedRouteLocations[i + 1];

      // Fetch route segment
      const { routePolyline, totalTime, routeGeometry } = await getRouteService(
        start,
        end,
      );

      // Check again after async operation
      if (signal?.aborted) {
        throw new Error("Request aborted");
      }

      // Store both polyline and time for this segment
      routeSegments.push({
        polyline: routePolyline,
        totalTime: totalTime,
        routeGeometry: routeGeometry,
      });

      // Notify progress
      if (onSegmentFetch) {
        onSegmentFetch(); // <-- Notify one segment completed
      }

      // Throttle requests
      if (i < selectedRouteLocations.length - 2) {
        await delay(150);
      }
    }

    return routeSegments;
  } catch (e) {
    if (signal?.aborted) {
      console.log("Fetch was cancelled");
      return []; // or rethrow depending on your use case
    }

    throw e;
  }
};

export const getExistingRoutePolyline = async (routeGeometry: string) => {
  try {
    if (!routeGeometry) {
      return [];
    }

    const decode = decodeRoute(routeGeometry);

    return decode;
  } catch (e) {
    throw e;
  }
};

export const filteredRoutesWithChanges = (routes: any[]) => {
  return routes.filter((route) => {
    // Check if the route.id is 0
    const matchRouteId = route.realRouteId === 0;

    // Check if any of the routeLocations has id === 0
    const hasLocationWithId0 = route.routeLocations.some(
      (loc: any) => loc.id === 0,
    );

    // Return true if either condition is met
    return matchRouteId || hasLocationWithId0;
  });
};

export const removeSchoolLocationFromRouteLocation = (
  routeLocations: IRouteLocation[],
  routeType: string,
) => {
  if (routeType === "from_school" || routeType === "cca") {
    // Remove first location
    return routeLocations.slice(1);
  } else if (routeType === "to_school") {
    // Remove last location
    return routeLocations.slice(0, -1);
  }
};

export const processRoutesToRemoveSchoolLocation = (
  routes: any,
  routeType: string,
) => {
  return routes.map((route: any) => {
    if (routeType === "from_school" || routeType === "cca") {
      // Remove first location
      return {
        ...route,
        routeLocations: route.routeLocations.slice(1),
      };
    } else if (routeType === "to_school") {
      // Remove last location
      return {
        ...route,
        routeLocations: route.routeLocations.slice(0, -1),
      };
    }
    // Return unchanged route for other types
    return route;
  });
};

export const processRoutesToAddSchoolLocation = (
  routes: any,
  busSchedule: any,
  school: any,
) => {
  const schoolLocation = {
    lat: school.lat,
    lng: school.lng,
    street: school.street,
    unitNumber: school.unitNumber,
    blockNumber: school.blockNumber,
    building: school.building,
    address: school.address,
    postalCode: school.postalCode,
    arrivalTime: moment(busSchedule.arrivalTime, "HH:mm").format("HH:mm"),
    students: [],
    order: "",
  } as any;

  return routes.map((route: any) => {
    if (busSchedule.type === "from_school" || busSchedule.type === "cca") {
      // add first location
      return {
        ...route,
        routeLocations: [schoolLocation, ...route.routeLocations],
      };
    } else if (busSchedule.type === "to_school") {
      // add last location
      return {
        ...route,
        routeLocations: [...route.routeLocations, schoolLocation],
      };
    }
    // Return unchanged route for other types
    return route;
  });
};

const timeToSeconds = (timeStr: string) => {
  if (!timeStr) {
    return 0;
  }

  const [hours, minutes] = timeStr.split(":").map(Number);
  return hours * 3600 + minutes * 60;
};

const secondsToTime = (totalSeconds: number) => {
  const minutes = Math.floor(totalSeconds / 60);
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  return `${String(hours).padStart(2, "0")}:${String(remainingMinutes).padStart(
    2,
    "0",
  )}`;
};

export const calculateStartTimeAndEndTime = (
  totalDurationRoutesInSeconds: number,
  maxBusStopDurationInMinutes: number,
  totalBusStops: number,
  initialArrivalTime: string,
  direction: string,
) => {
  let startTime;
  let endTime;

  if (direction === "to_school") {
    // Go backward
    startTime = moment(initialArrivalTime, "HH:mm")
      .subtract(totalDurationRoutesInSeconds, "seconds")
      .subtract(maxBusStopDurationInMinutes * totalBusStops, "minutes")
      .format("HH:mm");
    endTime = initialArrivalTime;
  } else {
    // Go forward
    startTime = initialArrivalTime;
    endTime = moment(initialArrivalTime, "HH:mm")
      .add(totalDurationRoutesInSeconds, "seconds")
      .add(maxBusStopDurationInMinutes * totalBusStops, "minutes")
      .format("HH:mm");
  }

  return { startTime, endTime };
};

export const calculateArrivalTimes = (
  locations: any[],
  durations: any[],
  maxBusStopDurationInMinutes: number,
  initialArrivalTime: string,
  direction: string,
) => {
  if (!locations.length || !durations.length) return locations;

  const result = [...locations];
  let currentTime = timeToSeconds(initialArrivalTime);

  if (direction === "to_school") {
    // Go backward
    for (let i = result.length - 1; i >= 0; i--) {
      if (i < result.length - 1) {
        currentTime -= durations[i];
      }
      result[i].arrivalTime = secondsToTime(currentTime);

      if (i > 0) {
        currentTime -= maxBusStopDurationInMinutes * 60;
      }
    }
  } else {
    // Go forward
    for (let i = 0; i < result.length; i++) {
      if (i > 0) {
        currentTime += maxBusStopDurationInMinutes * 60;
        currentTime += durations[i - 1]; // add travel time to current stop
      }
      result[i].arrivalTime = secondsToTime(currentTime);
    }
  }

  return result;
};

export const generateRouteName = (type: string, routeNumber: number) => {
  let prefix;

  switch (type) {
    case "from_school":
      prefix = "PM";
      break;
    case "to_school":
      prefix = "AM";
      break;
    case "cca":
      prefix = "CCA";
      break;
    default:
      prefix = "Route";
      break;
  }

  return `${prefix} ${routeNumber}`;
};

export const isSchoolLocation = (
  index: number,
  routeLocationLength: number,
  routeType: string,
) =>
  routeType === "to_school" ? index === routeLocationLength - 1 : index === 0;

export const reformatSeconds = (seconds: number) => {
  if (typeof seconds !== "number" || seconds < 0) {
    return "Invalid time";
  }

  if (seconds < 60) {
    // Less than a minute, display in seconds
    return `1 minute`;
  } else if (seconds < 3600) {
    // Less than an hour, display in minutes
    const minutes = Math.floor(seconds / 60);
    return `${minutes} minute${minutes === 1 ? "" : "s"}`;
  } else {
    // An hour or more, display in hours, minutes
    const hours = Math.floor(seconds / 3600);
    const remainingMinutes = Math.floor((seconds % 3600) / 60);

    let result = `${hours} hour${hours === 1 ? "" : "s"}`;
    if (remainingMinutes > 0) {
      result += ` ${remainingMinutes} minute${
        remainingMinutes === 1 ? "" : "s"
      }`;
    }
    return result;
  }
};

export const calculateDurationInString = (
  startTime: string,
  endTime: string,
) => {
  const start = timeToSeconds(startTime);
  const end = timeToSeconds(endTime);
  return reformatSeconds(end - start);
};

export const calculateDurationInSeconds = (
  startTime: string,
  endTime: string,
) => {
  const start = timeToSeconds(startTime);
  const end = timeToSeconds(endTime);
  return end - start;
};

export const reOptimiseRouteLocation = async (
  school: ISchool,
  busSchedule: IBusSchedule,
  maxStopTime: number,
  routeLocations: IRouteLocation[],
) => {
  try {
    if (routeLocations.length === 0) {
      // return school location
      return [
        {
          lat: school.lat,
          lng: school.lng,
          street: school.street,
          unitNumber: school.unitNumber,
          blockNumber: school.blockNumber,
          building: school.building,
          address: school.address,
          postalCode: school.postalCode,
          arrivalTime: busSchedule.arrivalTime,
          students: [],
          studentIds: [],
          order: "",
        },
      ];
    }

    let resultRoutes = [];

    const transformRouteLocations = routeLocations.map((location) => {
      return {
        lat: location.lat,
        lng: location.lng,
        street: location.street,
        unitNumber: location.unitNumber,
        blockNumber: location.blockNumber,
        building: location.building,
        address: location.address,
        postalCode: location.postalCode,
        studentIds: location.students.map((student) => student.id),
      };
    });

    if (busSchedule.type === "to_school") {
      const requestJson = {
        destinationLocation: {
          lat: school.lat,
          lng: school.lng,
          street: school.street,
          unitNumber: school.unitNumber,
          blockNumber: school.blockNumber,
          building: school.building,
          address: school.address,
          postalCode: school.postalCode,
        },
        desiredArrivalTime: busSchedule.arrivalTime
          .split(":")
          .slice(0, 2)
          .join(":"),
        maxVehicleCapacity: Number(999),
        desiredRouteDuration: 999 * 60,
        maxStopTime: maxStopTime * 60,
        locations: transformRouteLocations,
      };

      const responseToSchool = await dataProvider.customCreate(
        "routeOptimization/pickUp",
        {
          data: requestJson,
        },
      );

      resultRoutes = JSON.parse(JSON.stringify(responseToSchool.data.routes));
    } else if (
      busSchedule.type === "from_school" ||
      busSchedule.type === "cca"
    ) {
      const requestJson = {
        originLocation: {
          lat: school.lat,
          lng: school.lng,
          street: school.street,
          unitNumber: school.unitNumber,
          blockNumber: school.blockNumber,
          building: school.building,
          address: school.address,
          postalCode: school.postalCode,
        },
        desiredDepartureTime: busSchedule.arrivalTime
          .split(":")
          .slice(0, 2)
          .join(":"),
        maxVehicleCapacity: Number(999),
        desiredRouteDuration: 999 * 60,
        maxStopTime: maxStopTime * 60,
        locations: transformRouteLocations,
      };

      const responseFromSchool = await dataProvider.customCreate(
        "routeOptimization/dropOff",
        {
          data: requestJson,
        },
      );

      resultRoutes = JSON.parse(JSON.stringify(responseFromSchool.data.routes));
    }

    if (resultRoutes.length > 0) {
      const studentIds = resultRoutes[0].locations.flatMap(
        (location: any) => location.studentIds || [],
      );

      const { data: students } = await dataProvider.getMany("students", {
        ids: studentIds,
      });

      resultRoutes[0].locations = resultRoutes[0].locations.map(
        (location: any, index: number) => {
          location.id = 0;
          // change studentIds from locations to students
          location.students = Array.isArray(location.studentIds)
            ? students.filter((student: IStudent) =>
                (location.studentIds as number[]).includes(student.id),
              )
            : [];

          // reoder the order
          if (busSchedule.type === "to_school") {
            location.order = index + 1;
          } else {
            location.order = index;
          }
          return location;
        },
      );

      return resultRoutes[0].locations;
    } else {
      throw new Error("Failed to reoptimise route location");
    }
  } catch (error) {
    console.error(error);
    throw new Error("Failed to reoptimise route location");
  }
};

export const versioningRoutePlan = (routes: any, droppedLocations: any) => {
  const stored = localStorage.getItem("routePlanVersion");
  let versioningData: Array<{
    id: string;
    data: any;
    createdAt: string;
    selected: boolean;
  }> = [];

  if (stored) {
    try {
      versioningData = JSON.parse(stored);
      let hasSelected = false;
      versioningData = versioningData.map((v) => {
        if (v.selected && !hasSelected) {
          hasSelected = true;
          return v;
        }
        return { ...v, selected: false };
      });
    } catch (e) {
      console.warn("Failed to parse routePlanVersion. Starting fresh.");
    }
  }

  // Determine new version ID
  let newId: string;
  if (versioningData.length === 0) {
    newId = "Original";
  } else {
    const versionCount = versioningData.filter(
      (v) => v.id !== "Original",
    ).length;
    newId = `V${versionCount + 1}`;
  }

  // Deselect all existing versions
  versioningData = versioningData.map((v) => ({ ...v, selected: false }));

  // Add new version and mark it as selected
  const newVersion = {
    id: newId,
    data: {
      routes,
      droppedLocations,
    },
    createdAt: new Date().toISOString(),
    selected: true,
  };

  versioningData.push(newVersion);

  localStorage.setItem("routePlanVersion", JSON.stringify(versioningData));
};

export const setSelectedRoutePlanVersion = (id: string) => {
  const stored = localStorage.getItem("routePlanVersion");
  if (!stored) return;

  try {
    const versioningData = JSON.parse(stored);
    const updated = versioningData.map((v: any) => ({
      ...v,
      selected: v.id === id,
    }));

    localStorage.setItem("routePlanVersion", JSON.stringify(updated));

    const { data } = updated.find((v: any) => v.id === id);

    return data;
  } catch (e) {
    throw new Error("Failed to select version:" + e);
  }
};

export const getSelectedRoutePlanVersion = () => {
  const stored = localStorage.getItem("routePlanVersion");
  if (!stored) return null;

  try {
    const versions = JSON.parse(stored);
    return versions.find((v: any) => v.selected) || null;
  } catch (e) {
    throw new Error("Failed to read selected version:" + e);
  }
};

export const loadRoutePlanVersion = () => {
  const stored = localStorage.getItem("routePlanVersion");
  if (!stored) return;

  try {
    const versions = JSON.parse(stored);
    return versions;
  } catch (e) {
    throw new Error("Failed to read selected version:" + e);
  }
};

export const updateRoutePlanVersion = (
  id: string,
  routes: any[],
  droppedLocations: any[],
) => {
  const stored = localStorage.getItem("routePlanVersion");
  if (!stored) {
    console.warn("No routePlanVersion found in localStorage");
    return false;
  }

  try {
    const versioningData = JSON.parse(stored);

    // Check if version with this ID exists
    const versionExists = versioningData.some((v: any) => v.id === id);
    if (!versionExists) {
      console.warn(`Version with id "${id}" does not exist`);
      return false;
    }

    // Update the data for the matching version
    const updated = versioningData.map((v: any) => {
      if (v.id === id) {
        return {
          ...v,
          data: {
            routes,
            droppedLocations,
          },
        };
      }
      return v;
    });

    localStorage.setItem("routePlanVersion", JSON.stringify(updated));
    return true;
  } catch (e) {
    throw new Error("Failed to update route plan version:" + e);
  }
};
